import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HelmetProvider } from "react-helmet-async";
import Navbar from './Navbar';
import SummaryApi from '../common';

// Local image paths (will be transformed to URLs by the environment)
const heroIllustration = 'Gemini_Generated_Image_4qd6so4qd6so4qd6 (1).png';
const libraryIllustration = 'Gemini_Generated_Image_4qd6so4qd6so4qd6 (2).png';
const notebookIllustration = 'Gemini_Generated_Image_4qd6so4qd6so4qd6.png';

// --- ICONS ---
const CheckIcon = () => (
  <svg className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const FeatureIcon = ({ icon }) => {
  const icons = {
    management: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
    analytics: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
      </svg>
    ),
    mobile: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
    storage: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7a2 2 0 012-2h12a2 2 0 012 2v2H4V7zm0 4h16v2H4v-2zm0 4h16v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2z" />
      </svg>
    ),
  };
  return <div className="w-16 h-16 bg-gradient-to-r from-orange-100 to-teal-100 text-orange-800 rounded-full flex items-center justify-center mb-5">
    {icons[icon] || icons['management']}
  </div>;
};

const StatIcon = ({ icon }) => {
  const icons = {
    totalBooks: (
      <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
    activeMembers: (
      <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-2.356M12 20h-5v-2a3 3 0 015.356-2.356M9 11a3 3 0 100-6 3 3 0 000 6zM15 11a3 3 0 100-6 3 3 0 000 6zM2 20h2v-2a3 3 0 015.356-2.356" />
      </svg>
    ),
    dailyTransactions: (
      <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m-5 3H4a2 2 0 01-2-2V7a2 2 0 012-2h5l4 4z" />
      </svg>
    ),
    customerSatisfaction: (
      <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.027a2 2 0 01-1.789-2.894l3.5-7A2 2 0 0114 10zM4 21V10a2 2 0 012-2h3" />
      </svg>
    ),
  };
  return <div className="text-teal-600 mb-4">{icons[icon]}</div>;
};

// --- UPDATED PRICING CARD ---
const PricingCard = ({ plan, isPopular }) => {
  const navigate = useNavigate();
  const [selectedDurationIndex, setSelectedDurationIndex] = useState(0);

  // Rename 'Both' to something more attractive
  const displayName = plan?.planType === "Both" ? "Premium All-in-One" : plan?.planType;

  const monthlyPlan = plan.plans.find(p => p.durationInMonths === 1);
  const baselinePrice = monthlyPlan ? monthlyPlan.price : (plan.plans[0].price / plan.plans[0].durationInMonths);
  const selectedPlan = plan.plans[selectedDurationIndex];
  const totalCostAtMonthlyRate = baselinePrice * selectedPlan.durationInMonths;
  const savings = totalCostAtMonthlyRate - selectedPlan.price;
  const savingsPercent = totalCostAtMonthlyRate > 0 ? (savings / totalCostAtMonthlyRate) * 100 : 0;

  const getDurationName = (months) => {
    if (months === 1) return "Monthly";
    if (months === 12) return "Annually";
    return `${months} Months`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      // --- Popular Card Styling ---
      className={`relative bg-white rounded-2xl p-8 flex flex-col transition-all duration-300 
        ${isPopular
          ? 'shadow-2xl border-2 border-orange-500 transform md:-translate-y-4 z-10 scale-105'
          : 'shadow-lg border border-stone-100 hover:shadow-xl'
        }`}
    >
      {/* --- Badge for Popular Plan --- */}
      {isPopular && (
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <span className="bg-orange-600 text-white text-sm font-bold px-4 py-1 rounded-full shadow-md uppercase tracking-wider">
            Best Value
          </span>
        </div>
      )}

      <h3 className="text-3xl font-bold text-stone-800 mb-4 text-center">{displayName}</h3>

      <div className="flex items-center justify-center p-1 bg-stone-100 rounded-lg mb-6">
        {plan?.plans?.map((p, index) => (
          <button
            key={index}
            onClick={() => setSelectedDurationIndex(index)}
            className={`w-full py-2 px-3 rounded-md text-xs sm:text-sm font-medium transition-all ${selectedDurationIndex === index
                ? 'bg-white text-orange-800 shadow'
                : 'text-stone-600 hover:bg-stone-200'
              }`}
          >
            {getDurationName(p.durationInMonths)}
          </button>
        ))}
      </div>

      <div className="flex items-baseline justify-center mb-6">
        <span className={`text-5xl font-extrabold ${isPopular ? 'text-orange-600' : 'text-stone-900'}`}>
          ₹{selectedPlan.price.toLocaleString('en-IN')}
        </span>
        <span className="text-stone-600 ml-2">
          / {selectedPlan.durationInMonths === 1 ? "mo" : "yr"}
        </span>
      </div>

      <div className="text-center mb-8 h-6">
        {savingsPercent > 1 ? (
          <span className={`inline-block text-sm font-medium px-3 py-1 rounded-full ${isPopular ? 'bg-orange-100 text-orange-800' : 'bg-teal-100 text-teal-800'}`}>
            Save {Math.round(savingsPercent)}%
          </span>
        ) : (
          <span className="text-stone-400 text-sm">Standard Price</span>
        )}
      </div>

      <div className="flex-grow border-t border-stone-100 pt-6">
        <ul className="space-y-4 mb-8">
          {plan?.features?.map((feature, fIdx) => (
            <li key={fIdx} className="flex items-start text-stone-600">
              <CheckIcon />
              <span className="text-sm font-medium">{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => navigate('/register')}
        className={`w-full text-white px-6 py-4 rounded-xl font-bold shadow-lg transition-all text-lg mt-auto
          ${isPopular
            ? 'bg-gradient-to-r from-orange-600 to-red-600 shadow-orange-200'
            : 'bg-stone-800 hover:bg-stone-700'
          }`}
      >
        Choose {displayName}
      </motion.button>
    </motion.div>
  );
};

// --- HOME PAGE COMPONENT ---
const HomePage = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);

  const fadeInView = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.7, ease: "easeOut" },
    viewport: { once: true, amount: 0.2 }
  };

  const fectchPlans = async () => {
    try {
      const response = await fetch(SummaryApi.getAllPlans.url, {
        method: SummaryApi.getAllPlans.method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        return console.log('failed to fetch plans', data.message);
      }

      // --- SORTING PLANS ---
      const rawPlans = data.plans || [];
      const bothPlan = rawPlans.find(p => p.planType === "Both");
      const otherPlans = rawPlans.filter(p => p.planType !== "Both");

      const sortedPlans = [
        otherPlans[0], // Left card
        bothPlan,      // Center card (Highlighted)
        otherPlans[1]  // Right card
      ].filter(Boolean);

      setPlans(sortedPlans);
    } catch (err) {
      console.log(`Error Fetching Plans`, err);
    }
  };

  useEffect(() => {
    fectchPlans();
  }, []);

  const stats = [
    { key: 'totalBooks', value: '10,000+', label: 'Total Books', icon: 'totalBooks' },
    { key: 'activeMembers', value: '2,500+', label: 'Active Members', icon: 'activeMembers' },
    { key: 'dailyTransactions', value: '500+', label: 'Daily Transactions', icon: 'dailyTransactions' },
    { key: 'customerSatisfaction', value: '98%', label: 'Customer Satisfaction', icon: 'customerSatisfaction' }
  ];

  const features = [
    {
      id: 1,
      title: 'Better Management',
      description: 'Manage subscriptions, book records, reading seat reservations, and account details effortlessly.',
      icon: 'management',
      color: 'from-orange-700 to-orange-500'
    },
    {
      id: 2,
      title: 'Smart Analytics',
      description: 'Make data-driven decisions with real-time insights and predictions.',
      icon: 'analytics',
      color: 'from-orange-700 to-teal-500'
    },
    {
      id: 3,
      title: 'Mobile Experience',
      description: 'Seamless access to your library through our intuitive mobile app.',
      icon: 'mobile',
      color: 'from-teal-500 to-orange-500'
    },
    {
      id: 4,
      title: 'Unlimited Data Storage',
      description: 'Store unlimited book records, member details, and analytics without worrying about limits.',
      icon: 'storage',
      color: 'from-teal-700 to-orange-500'
    }
  ];

  return (
    <main className="min-h-screen bg-stone-50 overflow-hidden">
      <HelmetProvider>
        <title>MyReading | Personal Library Management Made Simple</title>
        <meta
          name="description"
          content="MyReading helps individuals manage their personal or small libraries online. Track books, members, reading seats, and lending history with ease."
        />
      </HelmetProvider>

      {/* ========================== HERO ========================== */}
      <header className="relative min-h-screen bg-stone-50 overflow-hidden">
        <Navbar />

        {/* Decorative blobs (unchanged) */}
        <div className="absolute inset-0 z-0">
          <motion.div
            className="absolute -top-1/4 -left-1/4 w-1/2 h-1/2 opacity-15"
            animate={{ scale: [1, 1.2, 1], rotate: [0, 180, 360] }}
            transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
          >
            <div className="w-full h-full bg-gradient-to-br from-orange-400 to-orange-600 rounded-full blur-3xl" />
          </motion.div>
          <motion.div
            className="absolute -bottom-1/4 -right-1/4 w-1/2 h-1/2 opacity-15"
            animate={{ scale: [1.2, 1, 1.2], rotate: [180, 360, 180] }}
            transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
          >
            <div className="w-full h-full bg-gradient-to-br from-teal-400 to-teal-600 rounded-full blur-3xl" />
          </motion.div>
        </div>

        <div className="relative min-h-screen flex items-center z-10">
          <div className="container mx-auto mb-8 px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

              {/* Text column */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9 }}
                className="text-center lg:text-left"
              >
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-stone-900 mb-6 leading-tight pt-24 ">
                  Manage Your Personal Library
                  <span className="block bg-gradient-to-r pt-2 from-orange-800 to-orange-900 pb-3 text-transparent bg-clip-text">
                    Easily and Efficiently
                  </span>
                </h1>
                <p className="text-xl md:text-2xl text-stone-700 mb-8 max-w-3xl">
                  Organize your books, track lending, manage members, and reserve reading seats in one simple dashboard.
                </p>

                <div className="flex items-center gap-4 justify-center lg:justify-start">
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    onClick={() => navigate('/register')}
                    className="bg-teal-600 text-white px-7 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors text-lg shadow-lg"
                  >
                    Get Started
                  </motion.button>

                  <button
                    onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                    className="bg-white border border-stone-200 px-6 py-3 rounded-lg font-medium text-stone-800 hover:shadow-md"
                  >
                    View Plans
                  </button>
                </div>

              </motion.div>

              {/* Illustration column */}
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.9 }}
                className="flex items-center justify-center lg:justify-end pt-12 lg:pt-24"
              >
                <div className="w-full max-w-lg bg-white/80 rounded-3xl p-6 shadow-2xl border border-stone-100">
                  <img
                    src={heroIllustration}
                    alt="cloud upload illustration"
                    className="w-full h-auto object-contain rounded-xl"
                    loading="lazy"
                  />
                </div>
              </motion.div>

            </div>
          </div>
        </div>
      </header>

      {/* ========================== ANALYTICS ========================== */}
      <section
        id="analytics"
        className="bg-stone-100 py-24 relative overflow-hidden"
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_-20%,_#c2410c,_transparent_70%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_120%,_#0d9488,_transparent_70%)]" />
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.h2 {...fadeInView} className="text-4xl md:text-5xl font-bold mb-6 text-stone-900">
            Track Everything with
            <span className="block bg-gradient-to-r from-orange-800 to-orange-900 text-transparent pb-2 bg-clip-text">
              Real-time Insights
            </span>
          </motion.h2>
          <motion.p {...fadeInView} transition={{ ...fadeInView.transition, delay: 0.2 }} className="text-xl text-stone-700 max-w-3xl mx-auto leading-relaxed mb-16">
            Understand your library’s performance and make smarter decisions with built-in analytics.
          </motion.p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.1 + 0.3 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-orange-500/5 rounded-2xl blur-xl transition-all duration-300 group-hover:bg-orange-500/10 group-hover:blur-2xl" />
                <div className="relative bg-white/80 backdrop-blur-md rounded-2xl p-8 border border-stone-200 shadow-sm transition-all duration-300 group-hover:shadow-lg">
                  <StatIcon icon={stat.icon} />
                  <div className="text-4xl mb-3 bg-gradient-to-r from-teal-600 to-teal-700 text-transparent bg-clip-text font-bold">
                    {stat.value}
                  </div>
                  <div className="text-stone-700 capitalize text-lg">
                    {stat.label}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========================== FEATURES + LIBRARY ILLUSTRATION ========================== */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <motion.h2 {...fadeInView} className="text-3xl md:text-4xl font-bold mb-4 text-stone-900">
                Features that make reading joyful
              </motion.h2>
              <motion.p {...fadeInView} transition={{ ...fadeInView.transition, delay: 0.1 }} className="text-stone-700 mb-8">
                Everything from member management to seat reservations — built to save time and help communities grow.
              </motion.p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {features.map((feature) => (
                  <motion.div key={feature.id} whileHover={{ y: -6 }} className="relative bg-white/80 backdrop-blur-md rounded-xl p-6 border border-stone-100 shadow-sm">
                    <FeatureIcon icon={feature.icon} />
                    <h3 className="text-lg font-semibold text-orange-800">{feature.title}</h3>
                    <p className="text-stone-600 mt-2">{feature.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }} className="flex justify-center lg:justify-end">
              <div className="w-full max-w-md bg-white/80 rounded-3xl p-6 shadow-2xl border border-stone-100">
                <img src={libraryIllustration} alt="library shelf illustration" className="w-full h-auto object-contain rounded-xl" loading="lazy" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========================== SEO CONTENT SECTION + NOTEBOOK ILLUSTRATION ========================== */}
      <motion.section {...fadeInView} className="bg-white py-16 text-center px-4">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-3xl font-bold text-stone-900 mb-4">What Makes MyReading Unique?</h2>
            <p className="text-stone-700 text-lg leading-relaxed">
              MyReading is designed for individuals and small communities who love books. Whether you run a private library, book café, or reading club, you can easily manage your collection, track borrowed books, monitor members, and even manage reading space reservations — all in one intuitive dashboard.
            </p>
          </div>

          <div className="flex justify-center lg:justify-end">
            <div className="w-full max-w-sm bg-white/80 rounded-3xl p-6 shadow-2xl border border-stone-100">
              <img src={notebookIllustration} alt="notebook planning illustration" className="w-full h-auto object-contain rounded-xl" loading="lazy" />
            </div>
          </div>
        </div>
      </motion.section>

      {/* ========================== PRICING SECTION ========================== */}
      <section id='pricing' className="bg-stone-100 py-24">
        <div className="container mx-auto px-4">
          <motion.h2 {...fadeInView} className="text-4xl md:text-5xl font-bold mb-6 text-stone-900 text-center">
            Choose Your
            <span className="block bg-gradient-to-r from-orange-800 to-orange-900 text-transparent pb-2 bg-clip-text">
              Subscription Plan
            </span>
          </motion.h2>
          <motion.p {...fadeInView} transition={{ ...fadeInView.transition, delay: 0.2 }} className="text-xl text-stone-700 text-center max-w-2xl mx-auto leading-relaxed mb-16">
            Select a plan and duration that best fits your needs.
          </motion.p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center pt-8">
            {plans?.map((plan, idx) => (
              <PricingCard
                key={idx}
                plan={plan}
                // --- Highlight if it is the 'Both' plan ---
                isPopular={plan.planType === "Both"}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ========================== CTA ========================== */}
      <section id='about' className="bg-stone-800 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <motion.h2 {...fadeInView} className="text-4xl md:text-5xl font-bold mb-6">Ready to Modernize Your Library?</motion.h2>
          <motion.p {...fadeInView} transition={{ ...fadeInView.transition, delay: 0.2 }} className="text-xl text-stone-300 mb-10">Join thousands of library owners already simplifying their management with MyReading.</motion.p>
          <motion.button
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(13, 148, 136, 0.5)' }}
            whileTap={{ scale: 0.95 }}
            transition={{ delay: 0.4 }}
            onClick={() => navigate('/register')}
            className="bg-teal-600 text-white px-10 py-4 rounded-lg font-medium hover:bg-teal-700 transition-colors text-lg shadow-lg"
          >
            Get Started Today
          </motion.button>
        </div>
      </section>

      {/* ========================== FOOTER ========================== */}
      <footer className="bg-stone-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">

            <motion.div {...fadeInView}>
              <h3 className="text-2xl font-semibold mb-4 text-orange-500">About</h3>
              <p className="text-stone-300 leading-relaxed">
                MyReading is your complete library command center. It's an intuitive platform designed to
                help you effortlessly manage your entire book collection, monitor all member transactions, and
                even organize your physical reading space with our seating reservation feature.
              </p>
            </motion.div>

            <motion.div {...fadeInView} transition={{ ...fadeInView.transition, delay: 0.2 }}>
              <h3 className="text-2xl font-semibold mb-4 text-orange-500">Contact</h3>
              <p className="text-stone-300 leading-relaxed">
                Email: info@shivaytech.com<br />
                Location: Beed
              </p>
            </motion.div>
            <motion.div {...fadeInView} transition={{ ...fadeInView.transition, delay: 0.4 }}>
              <h3 className="text-2xl font-semibold mb-4 text-orange-500">Quick Links</h3>
              <ul className="space-y-3">
                <li>
                  <button
                    onClick={() => navigate('/login')}
                    className="text-stone-300 hover:text-orange-500 transition-colors"
                  >
                    Login
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/register')}
                    className="text-stone-300 hover:text-orange-500 transition-colors"
                  >
                    Register
                  </button>
                </li>
              </ul>
            </motion.div>
            <motion.div {...fadeInView} transition={{ ...fadeInView.transition, delay: 0.4 }}>
              <h3 className="text-2xl font-semibold mb-4 text-orange-500">Legal</h3>
              <ul className="space-y-3">
                <li>
                  <button
                    onClick={() => navigate('/privacy-policy')}
                    className="text-stone-300 hover:text-orange-500 transition-colors"
                  >
                    Privacy Policy
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/terms-and-condition')}
                    className="text-stone-300 hover:text-orange-500 transition-colors"
                  >
                    Terms and Condition
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/refund-and-cancellation')}
                    className="text-stone-300 hover:text-orange-500 transition-colors"
                  >
                    Refund Policy
                  </button>
                </li>
              </ul>
            </motion.div>
          </div>
          <div className="border-t border-stone-700 mt-12 pt-8 text-center">
            <p className="text-stone-400">© {new Date().getFullYear()} MyReading. All rights reserved by ShivayTech.</p>
          </div>
        </div>
      </footer>
    </main>
  );
};

export default HomePage;
